import ProductCard from '@blocks/shop/product-card'
import Photo from '@components/photo'
import SimpleLink from '@components/simple-link'
import { type SanityMenuFeaturedItemLink } from '@data/sanity/queries/types/link'
import { type SanityProductFragment } from '@data/sanity/queries/types/product'
import { type SanityMenuFeaturedItem } from '@data/sanity/queries/types/site'
import {
  type PageType,
  type Slugs,
  getFormattedQueryString,
  getLinkPageUrl,
} from '@lib/routes'
import { StringsContext } from '@lib/strings-context'
import cx from 'classnames'
import { useContext } from 'react'

interface FeaturedItemsProps {
  items: SanityMenuFeaturedItem[]
  onClick?: () => void
  className?: string
}

const getIsProductType = (
  link: SanityMenuFeaturedItemLink,
): link is SanityProductFragment => {
  return link._type === 'product'
}

const FeaturedItems = ({ items, onClick, className }: FeaturedItemsProps) => {
  const strings = useContext(StringsContext)

  if (items.length === 0) {
    return null
  }

  const hasOnlyNonProductItems = items.every(
    (item) => !getIsProductType(item.link),
  )

  return (
    <div className={cx('grid grid-cols-3', className)}>
      {items.map((item, index) => {
        const key = `${index}-${item.link._type}-featured-menu-item-${item.link.title}`

        const isWide =
          (hasOnlyNonProductItems && items.length < 3 && index == 1) ||
          (!hasOnlyNonProductItems && items.length < 3)
        const isFullWidth = hasOnlyNonProductItems && items.length === 1

        if (getIsProductType(item.link)) {
          return (
            <ProductCard
              key={key}
              product={item.link as SanityProductFragment}
              showThumbnails
              showPrice
              onClick={onClick}
              titleClassName="!pl-0"
            />
          )
        }

        let pageType: PageType = item.link._type as PageType
        let slugs: Slugs = item.link.slug?.current ?? null

        if (item.link._type === 'collection' && item.collectionLinkContext) {
          if (item.collectionLinkContext === 'shopAll') {
            pageType = 'shopPageChild'
          }

          if (item.collectionLinkContext === 'sale') {
            pageType = 'shopSalePageChild'
          }

          if (
            item.collectionLinkContext === 'parent' &&
            item.parentCollection?.slug?.current
          ) {
            pageType = 'collectionChild'
            slugs = [
              item.parentCollection.slug.current,
              item.link.slug?.current ?? '',
            ]
          }
        }

        const url = `${getLinkPageUrl(
          pageType,
          slugs,
        )}${getFormattedQueryString(item.link.queryString)}`

        const image = item.image
          ? item.image
          : item.link.image
            ? item.link.image
            : null

        const hoverText =
          item.link._type === 'blogPost'
            ? strings.blogPostReadMore
            : item.link._type === 'collection'
              ? strings.buttonShopNow
              : null

        return (
          <SimpleLink
            key={key}
            href={url}
            className={cx('flex flex-col group', {
              'col-span-2': isWide,
              'col-span-3': isFullWidth,
            })}
          >
            {!!image && (
              <Photo
                image={{
                  ...image,
                  customRatio: isWide ? 10 / 7 : isFullWidth ? 28 / 13 : 5 / 7,
                }}
                imageClassName="h-full w-full object-cover"
                fadeIn
              />
            )}

            <h3 className="py-3 pr-3">
              {item.title ?? item.link.title}{' '}
              {!!hoverText && (
                <span className="hidden group-hover:inline">— {hoverText}</span>
              )}
            </h3>
          </SimpleLink>
        )
      })}
    </div>
  )
}

export default FeaturedItems
